import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Stack,
    Chip,
    Box,
    Typography,
    CircularProgress,
    Select,
} from "@mui/material";
import { useSelector } from "react-redux";
import EnhancedAxios from "../../axios/EnhancedAxios";
import { errorAlert } from "../../utils/alerts/alerts";

type Profile = {
    id: number;
    name: string;
};

type Group = {
    id: number;
    name: string;
};

type User = {
    id: number;
    username: string;
    name?: string;
};

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onUsernamesSelected: (usernames: string[]) => void;
    existingUsernames: string[];
};

const UsernameSelectorModal = ({
    open,
    setOpen,
    onUsernamesSelected,
    existingUsernames,
}: Props) => {
    const userToken = useSelector((state: any) => state.user.userToken);

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);

    const [selectedProfileId, setSelectedProfileId] = useState<string>("");
    const [selectedGroupId, setSelectedGroupId] = useState<string>("");

    const [loadingProfiles, setLoadingProfiles] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Fetch profiles on component mount
    useEffect(() => {
        if (open) {
            fetchProfiles();
            fetchGroups();
        }
    }, [open]);

    const fetchProfiles = async () => {
        setLoadingProfiles(true);
        try {
            const response = await EnhancedAxios(null, userToken).post(
                "/sasv4/profiles"
            );
            setProfiles(response.data?.data || []);
        } catch (error: any) {
            errorAlert("خطأ في تحميل الملفات الشخصية");
            console.error("Error fetching profiles:", error);
        } finally {
            setLoadingProfiles(false);
        }
    };

    const fetchGroups = async () => {
        setLoadingGroups(true);
        try {
            const response = await EnhancedAxios(null, userToken).post(
                "/sasv4/groups"
            );
            setGroups(response.data?.data || []);
        } catch (error: any) {
            errorAlert("خطأ في تحميل المجموعات");
            console.error("Error fetching groups:", error);
        } finally {
            setLoadingGroups(false);
        }
    };

    const fetchUsers = async () => {
        if (!selectedProfileId && !selectedGroupId) {
            errorAlert("يرجى اختيار ملف شخصي أو مجموعة");
            return;
        }

        setLoadingUsers(true);
        try {
            const requestData: any = {};
            if (selectedProfileId) requestData.profile_id = selectedProfileId;
            if (selectedGroupId) requestData.group_id = selectedGroupId;

            const response = await EnhancedAxios(null, userToken).post(
                "/sasv4/users",
                requestData
            );
            const fetchedUsers = response.data?.data || [];
            setUsers(fetchedUsers);

            // Automatically select all users that are not already in existingUsernames
            const newUsernames = fetchedUsers
                .map((user: User) => user.username)
                .filter(
                    (username: string) => !existingUsernames.includes(username)
                );
            setSelectedUsernames(newUsernames);
        } catch (error: any) {
            errorAlert("خطأ في تحميل المستخدمين");
            console.error("Error fetching users:", error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleUserToggle = (username: string) => {
        if (selectedUsernames.includes(username)) {
            // Deselect the username
            setSelectedUsernames(
                selectedUsernames.filter((u) => u !== username)
            );
        } else if (!existingUsernames.includes(username)) {
            // Select the username if it's not already in existing usernames
            setSelectedUsernames([...selectedUsernames, username]);
        }
    };

    const handleRemoveSelectedUsername = (username: string) => {
        setSelectedUsernames(selectedUsernames.filter((u) => u !== username));
    };

    const handleConfirm = () => {
        onUsernamesSelected(selectedUsernames);
        handleClose();
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedUsernames([]);
        setUsers([]);
        setSelectedProfileId("");
        setSelectedGroupId("");
    };

    const availableUsers = users.filter(
        (user) => !existingUsernames.includes(user.username)
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>اختيار أسماء المستخدمين</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Profile Selection */}
                    <TextField
                        select
                        label="اختيار الملف الشخصي"
                        value={selectedProfileId}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                        fullWidth
                        disabled={loadingGroups}
                    >
                        <MenuItem value="">-- اختر ملف شخصي --</MenuItem>
                        {profiles.map((profile) => (
                            <MenuItem key={profile.id} value={profile.id}>
                                {profile.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Group Selection */}
                    <TextField
                        select
                        label="اختيار المجموعة"
                        value={selectedGroupId}
                        onChange={(e) => setSelectedGroupId(e.target.value)}
                        fullWidth
                        disabled={loadingGroups}
                    >
                        <MenuItem value="">-- اختر مجموعة --</MenuItem>
                        {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Fetch Users Button */}
                    <Button
                        variant="contained"
                        onClick={fetchUsers}
                        disabled={
                            (!selectedProfileId && !selectedGroupId) ||
                            loadingUsers
                        }
                        startIcon={
                            loadingUsers ? <CircularProgress size={20} /> : null
                        }
                    >
                        {loadingUsers ? "جاري التحميل..." : "جلب المستخدمين"}
                    </Button>

                    {/* Users List */}
                    {users.length > 0 && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="h6">
                                    المستخدمون المتاحون:
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            const allAvailableUsernames =
                                                availableUsers.map(
                                                    (user) => user.username
                                                );
                                            setSelectedUsernames(
                                                allAvailableUsernames
                                            );
                                        }}
                                        disabled={
                                            selectedUsernames.length ===
                                            availableUsers.length
                                        }
                                    >
                                        تحديد الكل
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setSelectedUsernames([])}
                                        disabled={
                                            selectedUsernames.length === 0
                                        }
                                    >
                                        إلغاء تحديد الكل
                                    </Button>
                                </Stack>
                            </Stack>
                            <Stack
                                spacing={1}
                                sx={{ maxHeight: 200, overflow: "auto" }}
                            >
                                {availableUsers.map((user) => {
                                    const isSelected =
                                        selectedUsernames.includes(
                                            user.username
                                        );
                                    return (
                                        <Button
                                            key={user.id}
                                            variant={
                                                isSelected
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            color={
                                                isSelected
                                                    ? "primary"
                                                    : "inherit"
                                            }
                                            onClick={() =>
                                                handleUserToggle(user.username)
                                            }
                                            sx={{
                                                justifyContent: "flex-start",
                                                opacity: isSelected ? 1 : 0.6,
                                            }}
                                        >
                                            {isSelected ? "✓ " : "○ "}
                                            {user.username}{" "}
                                            {user.name && `(${user.name})`}
                                        </Button>
                                    );
                                })}
                                {availableUsers.length === 0 && (
                                    <Typography color="text.secondary">
                                        جميع المستخدمين مضافون بالفعل
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Selected Usernames */}
                    {selectedUsernames.length > 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                المستخدمون المحددون:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {selectedUsernames.map((username) => (
                                    <Chip
                                        key={username}
                                        label={username}
                                        onDelete={() =>
                                            handleRemoveSelectedUsername(
                                                username
                                            )
                                        }
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>إلغاء</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={selectedUsernames.length === 0}
                >
                    إضافة المستخدمين المحددين ({selectedUsernames.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UsernameSelectorModal;
